import { phishingSignsLabels } from '@/client/campaigns/types/campaigns.type';
import {
  DashboardFilterType,
  DepartmentPerformanceReport,
  GroupedByEntityFilterType,
  PhishingSignReports,
} from '@/client/reports/types';
import { TranslationFunctionType } from '@/common/types';
import moment from 'moment';

export const parsePeriodToDate = (period?: string): Date | undefined => {
  if (!period) {
    return undefined;
  }

  const trimmedPeriod = period.trim();

  const isoMoment = moment(trimmedPeriod, moment.ISO_8601, true);
  if (isoMoment.isValid()) {
    return isoMoment.toDate();
  }

  const supportedFormats = ['MM-YYYY', 'YYYY-MM', 'YYYY-MM-DD HH:mm:ssZ'];
  for (const format of supportedFormats) {
    const parsedMoment = moment(trimmedPeriod, format, true);
    if (parsedMoment.isValid()) {
      return parsedMoment.toDate();
    }
  }

  const fallbackDate = new Date(trimmedPeriod);
  return isNaN(fallbackDate.getTime()) ? undefined : fallbackDate;
};

export const getTimeBasedLabels = (
  fromDate: Date | undefined,
  toDate: Date | undefined,
  reportData: any[] | undefined,
  t: TranslationFunctionType,
  isDisabled = false,
) => {
  const labels: { [key: number]: string }[] = [];
  const monthNames = t('monthNamesShort', { returnObjects: 1 });
  let startDate: Date | undefined = undefined;
  let endDate: Date | undefined = undefined;

  if (!fromDate || !toDate) {
    if (!reportData || reportData.length === 0) {
      return [];
    }

    if (!fromDate && !toDate && !isDisabled) {
      const yearsWithData = reportData
        .map((item) => {
          const parsedDate = parsePeriodToDate(item?.period);
          return parsedDate ? new Date(parsedDate.getFullYear(), 0, 1) : null;
        })
        .filter((item): item is Date => !!item)
        .sort((a, b) => a.getTime() - b.getTime());

      if (yearsWithData.length === 0) {
        return [];
      }

      startDate = yearsWithData[0];
      endDate = yearsWithData[yearsWithData.length - 1];

      const yearIndex = startDate.getFullYear();
      const yearCount = endDate.getFullYear() - startDate.getFullYear() + 1;

      for (let i = 0; i < yearCount; i++) {
        const currentYear = yearIndex + i;
        labels.push({ [currentYear]: currentYear.toString() });
      }

      return labels;
    }

    const existingMonths = reportData
      .map((item) => {
        const parsedDate = parsePeriodToDate(item?.period);
        return parsedDate
          ? new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1)
          : null;
      })
      .filter((item): item is Date => !!item)
      .sort((a, b) => a.getTime() - b.getTime());

    if (existingMonths.length === 0) {
      return [];
    }

    startDate = existingMonths[0];
    endDate = new Date();
  } else {
    startDate = new Date(fromDate);
    endDate = new Date(toDate);
  }

  startDate.setDate(1);
  endDate.setDate(1);

  const monthsDiff =
    (endDate.getFullYear() - startDate.getFullYear()) * 12 +
    (endDate.getMonth() - startDate.getMonth()) +
    1;

  for (let i = 0; i < monthsDiff; i++) {
    const currentDate = new Date(startDate);
    currentDate.setMonth(startDate.getMonth() + i);
    const monthIndex = currentDate.getMonth();
    labels.push({ [monthIndex]: (monthNames as any)[monthIndex] });
  }

  return labels;
};

export const getKPITimeBasedLabels = (
  fromDate: Date | undefined,
  toDate: Date | undefined,
  reportData: any[] | undefined,
  t: TranslationFunctionType,
  isDisabled = false,
) => {
  const labels: { [key: number]: string }[] = [];
  const monthNames = t('monthNamesShort', { returnObjects: 1 });
  let startDate: Date | undefined = undefined;
  let endDate: Date | undefined = undefined;

  if (!fromDate || !toDate) {
    if (!reportData || reportData.length === 0) {
      return [];
    }

    if (isDisabled) {
      const existingMonths = reportData
        .map((item) => {
          const parsedDate = parsePeriodToDate(item?.period);
          return parsedDate
            ? new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1)
            : null;
        })
        .filter((item): item is Date => !!item)
        .sort((a, b) => a.getTime() - b.getTime());

      if (existingMonths.length === 0) {
        return [];
      }

      startDate = existingMonths[0];
      endDate = existingMonths[existingMonths.length - 1];
    } else {
      const existingMonths = reportData
        .map((item) => {
          const parsedDate = parsePeriodToDate(item?.period);
          return parsedDate
            ? new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1)
            : null;
        })
        .filter((item): item is Date => !!item)
        .sort((a, b) => a.getTime() - b.getTime());

      if (existingMonths.length === 0) {
        return [];
      }

      startDate = existingMonths[0];
      endDate = new Date();
    }
  } else {
    startDate = new Date(fromDate);
    endDate = new Date(toDate);
  }

  startDate.setDate(1);
  endDate.setDate(1);

  const monthsDiff =
    (endDate.getFullYear() - startDate.getFullYear()) * 12 +
    (endDate.getMonth() - startDate.getMonth()) +
    1;

  for (let i = 0; i < monthsDiff; i++) {
    const currentDate = new Date(startDate);
    currentDate.setMonth(startDate.getMonth() + i);
    const monthIndex = currentDate.getMonth();
    labels.push({ [monthIndex]: (monthNames as any)[monthIndex] });
  }

  return labels;
};

export const dataRangeOptions = (t: TranslationFunctionType) => [
  {
    label: t('phishing.analytics.dataRange.last12Months'),
    value: {
      from: moment()
        .endOf('month')
        .subtract(11, 'months')
        .startOf('month')
        .toDate(),
      to: moment().toDate(),
    },
  },
  {
    label: t('phishing.analytics.dataRange.allTime'),
    value: {},
  },
];

export const legendItems = (t: TranslationFunctionType) => [
  t('phishing.chart.reportRate'),
  t('phishing.chart.clickRate'),
  t('phishing.chart.submissionRate'),
  t('phishing.chart.reportRateTrend'),
  t('phishing.chart.clickRateTrend'),
  t('phishing.chart.submissionRateTrend'),
];

export const getPhishingSignsChartData = (
  t: TranslationFunctionType,
  phishingSigns?: PhishingSignReports[],
) => {
  if (!phishingSigns) {
    return {
      labels: [],
      reportedRateData: [],
      clickedRateData: [],
      submittedRateData: [],
      maxScale: 0,
      maxValue: 0,
      datasets: [],
    };
  }

  const labelsData = phishingSignsLabels(t);
  const labels = Object.values(labelsData);
  const labelsKeys = Object.keys(labelsData);

  const reportedRateData = labelsKeys.map((key) => {
    const sign = phishingSigns.find((s) => s.phishingSign === key);
    return sign ? Math.min(Number(sign.reportedRate), 100) : null;
  });

  const clickedRateData = labelsKeys.map((key) => {
    const sign = phishingSigns.find((s) => s.phishingSign === key);
    return sign ? Math.min(Number(sign.clickedRate), 100) : null;
  });

  const submittedRateData = labelsKeys.map((key) => {
    const sign = phishingSigns.find((s) => s.phishingSign === key);
    return sign ? Math.min(Number(sign.submittedRate), 100) : null;
  });

  const allValues = [
    ...reportedRateData,
    ...clickedRateData,
    ...submittedRateData,
  ];
  const maxValue = Math.max(
    ...(allValues.filter((v) => v !== null) as number[]),
    0,
  );

  const maxScale = Math.max(Math.min(Math.ceil(maxValue / 5) * 5, 100), 20);

  const backgroundData = labelsKeys.map((key) => {
    const sign = phishingSigns.find((s) => s.phishingSign === key);
    return sign ? maxScale : null;
  });

  const noDataData = labelsKeys.map((key) => {
    const sign = phishingSigns.find((s) => s.phishingSign === key);
    return sign ? 0 : maxScale;
  });

  const datasets = labels.map((label, index) => {
    const key = labelsKeys[index];
    const sign = phishingSigns.find((s) => s.phishingSign === key);

    if (!sign) {
      return {
        label,
        value: null,
      };
    }

    return {
      label,
      value: {
        reportedRate: sign.reportedRate
          ? Math.min(Number(sign.reportedRate), 100)
          : null,
        clickedRate: sign.clickedRate
          ? Math.min(Number(sign.clickedRate), 100)
          : null,
        submittedRate: sign.submittedRate
          ? Math.min(Number(sign.submittedRate), 100)
          : null,
        sentCount: sign.sentCount || 0,
        background: sign.sentCount ? maxScale : null,
        noData: sign.sentCount ? 0 : maxScale,
      },
    };
  });

  return {
    labels,
    reportedRateData,
    clickedRateData,
    submittedRateData,
    backgroundData,
    noDataData,
    maxScale,
    maxValue,
    datasets,
  };
};

export const DepartmentPerformanceMetrics = {
  CLICK_RATE: 'click-rate',
  DATA_SUBMISSION_RATE: 'data-submission-rate',
  REPORT_RATE: 'report-rate',
};

export const getPerformanceChartTitleAndDescription = (
  t: TranslationFunctionType,
  selectedDepartment: GroupedByEntityFilterType,
) => {
  switch (selectedDepartment) {
    case GroupedByEntityFilterType.DEPARTMENT:
      return {
        name: t('generic.department'),
        description: t('phishing.analytics.analysis.department.description'),
      };
    case GroupedByEntityFilterType.MANAGER:
      return {
        name: t('generic.manager'),
        description: t('phishing.analytics.analysis.manager.description'),
      };
    case GroupedByEntityFilterType.BRANCH:
      return {
        name: t('branch'),
        description: t('phishing.analytics.analysis.branch.description'),
      };
    case GroupedByEntityFilterType.GROUP:
      return {
        name: t('group'),
        description: t('phishing.analytics.analysis.group.description'),
      };
    default:
      return {
        name: t('generic.department'),
        description: t('phishing.analytics.analysis.department.description'),
      };
  }
};

export const getDepartmentPerformanceMetricOptions = (
  t: TranslationFunctionType,
) => [
  {
    label: t('phishing.chart.clickRate'),
    value: DepartmentPerformanceMetrics.CLICK_RATE,
  },
  {
    label: t('phishing.chart.submissionRate'),
    value: DepartmentPerformanceMetrics.DATA_SUBMISSION_RATE,
  },
  {
    label: t('phishing.chart.reportRate'),
    value: DepartmentPerformanceMetrics.REPORT_RATE,
  },
];

export const getDepartmentPerformanceMetricLabel = (
  t: TranslationFunctionType,
  metric: string,
) => {
  switch (metric) {
    case DepartmentPerformanceMetrics.CLICK_RATE:
      return t('phishing.chart.clickRate');
    case DepartmentPerformanceMetrics.DATA_SUBMISSION_RATE:
      return t('phishing.chart.submissionRate');
    case DepartmentPerformanceMetrics.REPORT_RATE:
      return t('phishing.chart.reportRate');
    default:
      return 'rate';
  }
};

export const getDepartmentsPerformanceChartData = (
  data: DepartmentPerformanceReport[] | undefined,
  metric: string,
) => {
  if (!data) return { dataLabels: [], dataValues: [] };
  let sortedData: DepartmentPerformanceReport[] = [];
  let labels: string[] = [];
  let values: number[] = [];

  switch (metric) {
    case DepartmentPerformanceMetrics.CLICK_RATE:
      sortedData = data.sort(
        (a, b) => Number(b.clickedRate) - Number(a.clickedRate),
      );
      labels = sortedData.map((item) => (item.name ? item.name : 'N/A'));
      values = sortedData.map((item) => Number(item.clickedRate));
      break;
    case DepartmentPerformanceMetrics.DATA_SUBMISSION_RATE:
      sortedData = data.sort(
        (a, b) => Number(b.submittedRate) - Number(a.submittedRate),
      );
      labels = sortedData.map((item) => (item.name ? item.name : 'N/A'));
      values = sortedData.map((item) => Number(item.submittedRate));
      break;
    case DepartmentPerformanceMetrics.REPORT_RATE:
      sortedData = data.sort(
        (a, b) => Number(a.reportedRate) - Number(b.reportedRate),
      );
      labels = sortedData.map((item) => (item.name ? item.name : 'N/A'));
      values = sortedData.map((item) => Number(item.reportedRate));
      break;
  }

  return {
    dataLabels: labels,
    dataValues: values,
  };
};

export const filterTypeOptions = (t: TranslationFunctionType) => [
  {
    label: t('phishing.analytics.filterData.all_data'),
    value: DashboardFilterType.ALL_DATA,
  },
  { label: t('filter.managers'), value: DashboardFilterType.MANAGERS },
  { label: t('filter.departments'), value: DashboardFilterType.DEPARTMENTS },
  { label: t('filter.branches'), value: DashboardFilterType.BRANCHES },
  { label: t('filter.groups'), value: DashboardFilterType.GROUPS },
];

export const groupedByEntityFilterTypeOptions = (
  t: TranslationFunctionType,
) => [
  {
    label: t('filter.departments'),
    value: GroupedByEntityFilterType.DEPARTMENT,
  },
  { label: t('filter.managers'), value: GroupedByEntityFilterType.MANAGER },
  { label: t('filter.branches'), value: GroupedByEntityFilterType.BRANCH },
  { label: t('filter.groups'), value: GroupedByEntityFilterType.GROUP },
];

export const getRadarChartTickCallback = (maxScale: number) => {
  return (value: number) => {
    if (!maxScale) return `${value}%`;

    const valuesToHide = [];
    for (let i = 20; i <= maxScale; i += 20) {
      valuesToHide.push(i);
    }

    if (valuesToHide.length === 0) return `${value}%`;

    const closestToMax = valuesToHide.reduce((prev, curr) =>
      Math.abs(curr - maxScale) < Math.abs(prev - maxScale) ? curr : prev,
    );

    return value === closestToMax ? '' : `${value}%`;
  };
};
